import { Box, Flex } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import React from 'react';

/**
 * Reskinned pagination — matches mockup `.pagination` / `.pagination__btn`:
 * 34px DS buttons (border + surface), active = primary, hover = canvas,
 * disabled .45, windowed page numbers with ellipsis, first/prev/next/last.
 * Props unchanged: { total, current, pageSize, onChange(page, pageSize), disabled }.
 */
const Paginate = ({ total, current, pageSize, onChange, disabled }) => {
  const PAGE_SIZE = pageSize || 10;
  const CURRENT = current || 1;
  const TOTAL = total || 0;
  const NUMBER_PAGE = Math.ceil(TOTAL / PAGE_SIZE);

  if (TOTAL === 0 || NUMBER_PAGE <= 1) return null;

  const go = (p) => !disabled && onChange && onChange(p, PAGE_SIZE);

  const btnBase = {
    minW: '34px',
    h: '34px',
    px: 2,
    borderRadius: 'sm',
    borderWidth: '1px',
    borderColor: 'border.default',
    bg: 'bg.surface',
    color: 'text.default',
    fontSize: 'sm',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s ease, border-color 0.15s ease',
    _hover: { bg: 'bg.canvas', borderColor: 'border.strong' },
  };

  const NavBtn = ({ to, isDisabled, children, label }) => (
    <Box
      as='button'
      aria-label={label}
      {...btnBase}
      opacity={isDisabled ? 0.45 : 1}
      pointerEvents={isDisabled ? 'none' : 'auto'}
      onClick={() => go(to)}
    >
      {children}
    </Box>
  );

  const PageBtn = ({ p }) => {
    const active = p === CURRENT;
    return (
      <Box
        as='button'
        {...btnBase}
        color={active ? 'primary.on' : 'text.default'}
        fontFamily='mono'
        bg={active ? 'primary.solid' : 'bg.surface'}
        borderColor={active ? 'primary.solid' : 'border.default'}
        _hover={active ? {} : { bg: 'bg.canvas', borderColor: 'border.strong' }}
        onClick={() => go(p)}
      >
        {p}
      </Box>
    );
  };

  // windowed: 1 … (cur-1 cur cur+1) … last
  const pages = [];
  const start = Math.max(2, CURRENT - 1);
  const end = Math.min(NUMBER_PAGE - 1, CURRENT + 1);
  pages.push(1);
  if (start > 2) pages.push('l-ell');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < NUMBER_PAGE - 1) pages.push('r-ell');
  pages.push(NUMBER_PAGE);

  return (
    <Flex align='center' justify='flex-end' wrap='wrap' gap={1} w='100%' px={4} py={3}>
      <NavBtn to={1} isDisabled={CURRENT === 1} label='First page'>
        <ChevronsLeft size={16} />
      </NavBtn>
      <NavBtn to={CURRENT - 1} isDisabled={CURRENT === 1} label='Previous page'>
        <ChevronLeft size={16} />
      </NavBtn>
      {pages.map((p, i) =>
        typeof p === 'number' ? (
          <PageBtn key={p} p={p} />
        ) : (
          <Box key={`${p}-${i}`} px={1} color='text.subtle' userSelect='none'>
            …
          </Box>
        )
      )}
      <NavBtn to={CURRENT + 1} isDisabled={CURRENT === NUMBER_PAGE} label='Next page'>
        <ChevronRight size={16} />
      </NavBtn>
      <NavBtn to={NUMBER_PAGE} isDisabled={CURRENT === NUMBER_PAGE} label='Last page'>
        <ChevronsRight size={16} />
      </NavBtn>
    </Flex>
  );
};

export default Paginate;
